import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Row,
  AutoComplete,
  Input,
  Typography,
  Layout,
  Divider,
  Badge,
  Avatar,
  Tooltip,
} from 'antd';
import {
  BellOutlined,
  SettingOutlined,
  MenuOutlined,
  CloseOutlined,
  UserOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { getRoutes, flatRoutes } from 'routes/index';
import logo from 'assets/logo-sigob.png';
import { seelectEntidad, selectPeriodoFiscalSelected, selectUser } from 'store/auth';
import { selectCollapsedSidebar, selectSidebarHidden } from 'store/app';
import { getConfiguradoresNombramientoUsuarioNotificar } from 'api/catalogos/ConfiguradorNombramientoUsuarioNotificar';
import { getNombramientosSinPaginar } from 'api/catalogos/Nombramientos';

const { Header } = Layout;
const { Text } = Typography;

const RENOVACION_POLLING_MS = 5 * 60 * 1000;

const LayoutHeader = ({
  isMobile,
  toggle,
  collapsed,
  onClickAvatar,
  onClickNotifications,
  onClickSetting,
}) => {
  const [value, setValue] = useState();
  const [renovacionCount, setRenovacionCount] = useState(0);
  const entidad = useSelector(seelectEntidad);
  const navigate = useNavigate();
  const collapsedSidebar = useSelector(selectCollapsedSidebar);
  const periodoFiscalSelected = useSelector(selectPeriodoFiscalSelected);
  const sidebarHidden = useSelector(selectSidebarHidden);
  const user = useSelector(selectUser);

  useEffect(() => {
    let cancelled = false;

    const checkRenovaciones = async () => {
      if (!user?.id) return;
      try {
        const configs = await getConfiguradoresNombramientoUsuarioNotificar({
          usuario: user.id,
          alerta: true,
          eliminado: false,
        });
        const hayConfig = Array.isArray(configs)
          ? configs.length > 0
          : (configs?.results?.length > 0);

        if (!hayConfig) {
          if (!cancelled) setRenovacionCount(0);
          return;
        }

        const pendientes = await getNombramientosSinPaginar({
          alerta_de_renovacion: true,
          estado_de_nombramiento: 2,
          eliminado: false,
        });
        if (!cancelled) {
          const count = Array.isArray(pendientes)
            ? pendientes.length
            : (pendientes?.count ?? 0);
          setRenovacionCount(count);
        }
      } catch (_e) {
        // No se interrumpe el polling: el siguiente tick reintenta. Si el
        // error es por sesión expirada, utils/api.js ya avisó y cerró
        // sesión; aquí solo dejamos rastro para diagnóstico.
        // eslint-disable-next-line no-console
        console.error('[polling renovaciones de nombramiento] error al consultar estado', _e);
      }
    };

    checkRenovaciones();
    const interval = setInterval(checkRenovaciones, RENOVACION_POLLING_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user?.id]);

  const routes = getRoutes();
  const flattedRoutes = flatRoutes(periodoFiscalSelected?.vigente
    ? routes : routes.filter((e, i) => i !== 2));
  // Si la página o alguno de sus módulos padre está oculto en el configurador de
  // sidebar (Configuración > Accesos > Permisos Sidebar), no debe salir en el buscador.
  const isPathHiddenFromSidebar = (path) => sidebarHidden.some(
    (hiddenPath) => path === hiddenPath || path.startsWith(`${hiddenPath}/`),
  );
  const visibleFlattedRoutes = flattedRoutes.filter((r) => !isPathHiddenFromSidebar(r.path));

  const ToggleSidebarButton = () => (
    React.createElement(MenuOutlined, {
      className: `toggle-sidebar-trigger ${collapsedSidebar ? 'close' : 'open'}`,
      style: {
        fontSize: '28px',
        lineHeight: '64px',
        padding: '0 26px',
      },
      onClick: toggle,
    })
  );

  const MobileToggleSidebarButton = () => (
    React.createElement(collapsed ? MenuOutlined : CloseOutlined, {
      className: 'toggle-sidebar-trigger',
      style: {
        fontSize: '22px',
        lineHeight: '64px',
        padding: '0 15px',
      },
      onClick: toggle,
    })
  );

  // eslint-disable-next-line react/prop-types
  const LinkTo = ({ to, title }) => (
    <span style={{ display: 'flex', flex: 1 }}>
      <span style={{ marginRight: 'auto' }}>
        {title}
      </span>
      <Link to={to} target="_blank" onClick={(e) => { e.stopPropagation(); }}>
        <Tooltip title="Abrir en nueva pestaña">
          <ArrowRightOutlined />
        </Tooltip>
      </Link>
    </span>
  );

  const getTitle = (item) => (
    <LinkTo to={item.path} title={item.title} />
  );

  const searchOptions = visibleFlattedRoutes.map((r) => ({
    path: r.path,
    tags: r.tags,
    value: r.path,
    title: r.title,
    label: getTitle(r),
  }));

  const UserAvatar = () => {
    let props;
    if (user?.foto) {
      props = {
        src: user?.foto,
      };
    } else {
      props = {
        icon: <UserOutlined />,
      };
    }
    return (
      <Avatar
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        size="large"
      />
    );
  };

  return (
    <Header
      style={{
        padding: '0',
        position: 'absolute',
        left: 0,
        right: 0,
      }}
    >
      <Row style={{ height: 64 }} justify="space-between">
        <div className="logo-sidebar">
          {!isMobile ? (
            <div className="logo-wrapper">
              <ToggleSidebarButton />
              <img
                src={entidad?.logotipo || logo}
                alt="logo"
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  navigate('/empleados/empleados');
                }}
                aria-hidden="true"
              />
            </div>
          ) : (<MobileToggleSidebarButton />)}
        </div>
        {!isMobile && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 'calc(100vw - 700px)',
          }}
          >
            <AutoComplete
              value={value}
              options={searchOptions}
              style={{ width: '80%', minWidth: 250, maxWidth: 350 }}
              onChange={setValue}
              onSelect={(val) => {
                navigate(val);
                setValue('');
              }}
              filterOption={(input, option) => {
                // eslint-disable-next-line no-param-reassign
                input = input?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                return option?.path.toLowerCase().includes(input)
                  || (option?.tags?.find((tag) => tag?.toLowerCase().includes(input)))
                  || option?.title?.toLowerCase()
                    .normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(input);
              }}
              popupClassName="wrap-text buscador-global__dropdown"
              onBlur={() => { setValue(''); }}
              onFocus={() => { setValue(''); }}
            >
              <Input placeholder="Buscar..." allowClear />
            </AutoComplete>
          </div>
        )}
        <div className="user-options">
          <Divider type="vertical" />
          {renovacionCount > 0 && (
            <Tooltip title="Nombramientos pendientes de renovar">
              <Badge count={renovacionCount} size="medium">
                <BellOutlined
                  onClick={() => navigate('/empleados/nombramientos?tab=renovacion')}
                />
              </Badge>
            </Tooltip>
          )}
          <Divider type="vertical" />
          <SettingOutlined
            onClick={onClickSetting}
          />
          <Divider type="vertical" />
          <div
            title={user.length > 12 ? user : ''}
            onClick={onClickAvatar}
            style={{ cursor: 'pointer' }}
            className="username"
            aria-hidden="true"
          >
            <UserAvatar />
            {user?.first_name && (
              <Text style={{ textTransform: 'capitalize' }}>
                {`${user.first_name.split(' ').shift()}` || ''}
              </Text>
            )}
          </div>
        </div>
      </Row>
    </Header>
  );
};

LayoutHeader.propTypes = {
  onClickAvatar: PropTypes.func.isRequired,
  onClickNotifications: PropTypes.func.isRequired,
  onClickSetting: PropTypes.func.isRequired,
  toggle: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  collapsed: PropTypes.bool,
};

LayoutHeader.defaultProps = {
  collapsed: false,
  isMobile: false,
};

export default LayoutHeader;
